import React, { useCallback, useRef } from 'react';
import classnames from 'classnames';

import { ScrollArrow } from './components/ScrollArrow';
import { useScroll } from './hooks/useScroll';
import { useScrollState } from './hooks/useScrollState';
import styles from './styles.module.css';
import type { ScrollableRowProps } from './types';

const DEFAULT_CONFIG = {
  behavior: 'smooth',
  multiplier: 0.8,
} as const;

const ScrollableRow = React.forwardRef<HTMLDivElement, ScrollableRowProps>((props, ref) => {
  const {
    children,
    className = '',
    regressionTag,
    scrollConfig = {},
    showArrows = true,
    showFade = true,
    testId = 'scrollable-row',
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const config = { ...DEFAULT_CONFIG, ...scrollConfig } as const;

  const { checkScroll, left: showLeftArrow, right: showRightArrow } = useScrollState({ containerRef });

  const scroll = useScroll({ containerRef, config });

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scroll('left');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scroll('right');
      }
    },
    [scroll],
  );

  if (!children) return null;

  return (
    <div
      aria-label="Scrollable content"
      className={classnames(styles.scrollableContainer, {
        [styles.showLeftFade]: showLeftArrow && showFade,
        [styles.showRightFade]: showRightArrow && showFade,
        [styles.noArrows]: !showArrows,
      })}
      data-regression-tag={regressionTag}
      role="region"
    >
      {showArrows && (
        <>
          <ScrollArrow direction="left" isVisible={showLeftArrow} onClick={() => scroll('left')} />
          <ScrollArrow direction="right" isVisible={showRightArrow} onClick={() => scroll('right')} />
        </>
      )}

      <div
        className={classnames(styles.scrollableRow, className)}
        data-testid={testId}
        onKeyDown={handleKeyDown}
        onScroll={checkScroll}
        ref={node => {
          if (ref) {
            if (typeof ref === 'function') ref(node);
            else ref.current = node;
          }
          containerRef.current = node;
        }}
        role="list"
        tabIndex={0}
      >
        {children}
      </div>
    </div>
  );
});

ScrollableRow.displayName = 'ScrollableRow';

export default ScrollableRow;
